import { useEffect, useState } from 'react'
import { useAccountCurrency } from '@/lib/use-account-currency'
import { type Resolver } from 'react-hook-form'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Form, FormControl, FormMessage, FormDialog, FormField, FormItem, FormLabel,
	Input,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import { useCreateFinanceTransactionMutation, useListFinanceInvoicesQuery } from '@/api'
import { usePersistedForm } from '@/lib/use-persisted-form'
import { extractErrorMessage } from '@/lib/error-message'
import { PAYMENT_METHOD_KEYS, type PaymentMethodType } from '@/types'
import { createTransactionSchema, DEFAULT_VALUES, type CreateTransactionFormValues } from './schema'

const DRAFT_KEY = 'finance:transaction:create'

const PAYMENT_METHODS: PaymentMethodType[] = ['KASSA', 'CASH', 'BANK_TRANSFER', 'CARD']

interface CreateTransactionDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
}

export function CreateTransactionDialog({ open, onOpenChange }: CreateTransactionDialogProps) {
	const { t } = useTranslation()
	const accountCurrency = useAccountCurrency()
	const { data: invoicesData } = useListFinanceInvoicesQuery({ status: 'PENDING', limit: 200 })
	const [createTransaction, { isLoading }] = useCreateFinanceTransactionMutation()
	const [hasError, setHasError] = useState(false)
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

	const { form, resetDraft } = usePersistedForm<CreateTransactionFormValues>({
		key:           DRAFT_KEY,
		defaultValues: DEFAULT_VALUES,
		resolver:      zodResolverI18n(createTransactionSchema) as Resolver<CreateTransactionFormValues>,
		mode:          'onBlur',
		active:        open,
	})

	// The parent keeps this dialog mounted, so error state would otherwise
	// survive close/reopen — clear it whenever the dialog opens.
	useEffect(() => {
		if (open) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setHasError(false)
			setErrorMsg(undefined)
		}
	}, [open])

	const invoices = invoicesData?.invoices ?? []
	const selectedInvoiceId = form.watch('invoiceId')
	const selectedInvoice = invoices.find((i) => i.id === selectedInvoiceId)

	async function onSubmit(values: CreateTransactionFormValues) {
		setHasError(false)
		setErrorMsg(undefined)
		try {
			await createTransaction({
				invoiceId:     values.invoiceId,
				amount:        String(values.amount),
				currency:      selectedInvoice?.currency ?? accountCurrency,
				paymentMethod: values.paymentMethod,
				...(values.externalRef && { externalRef: values.externalRef }),
			}).unwrap()
			resetDraft()
			form.reset(DEFAULT_VALUES)
			onOpenChange(false)
		} catch (err) {
			setHasError(true)
			setErrorMsg(extractErrorMessage(err))
		}
	}

	return (
		<Form {...form}>
			<FormDialog
				open={open}
				onOpenChange={onOpenChange}
				title={t('finances.transactionDialog.title')}
				onSubmit={form.handleSubmit(onSubmit)}
				submitLabel={t('finances.transactionDialog.submit')}
				cancelLabel={t('finances.transactionDialog.cancel')}
				closeAriaLabel={t('common.close')}
				isSubmitting={isLoading}
				isSubmitDisabled={!form.formState.isValid}
			>
				{hasError && (
					<Alert variant="destructive">
						<AlertCircleIcon className="size-4" />
						<AlertTitle>{t('finances.transactionDialog.errors.save')}</AlertTitle>
						{errorMsg && <AlertDescription>{errorMsg}</AlertDescription>}
					</Alert>
				)}

				<FormField
					control={form.control}
					name="invoiceId"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('finances.transactionDialog.invoice')}</FormLabel>
							<Select onValueChange={field.onChange} value={field.value}>
								<FormControl>
									<SelectTrigger width="full">
										<SelectValue placeholder={t('finances.transactionDialog.invoicePlaceholder')} />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									{invoices.map((inv) => (
										<SelectItem key={inv.id} value={inv.id}>
											{inv.title} — {inv.amount} {inv.currency}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>

				<div className="grid grid-cols-2 gap-3">
					<FormField
						control={form.control}
						name="amount"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('finances.transactionDialog.amount')}</FormLabel>
								<FormControl>
									<Input type="number" min={0} step="0.01" {...field} value={field.value ?? ''} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="paymentMethod"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('finances.transactionDialog.paymentMethod')}</FormLabel>
								<Select onValueChange={field.onChange} value={field.value}>
									<FormControl>
										<SelectTrigger width="full">
											<SelectValue placeholder={t('finances.transactionDialog.paymentMethodPlaceholder')} />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{PAYMENT_METHODS.map((m) => (
											<SelectItem key={m} value={m}>{t(PAYMENT_METHOD_KEYS[m])}</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<FormField
					control={form.control}
					name="externalRef"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('finances.transactionDialog.externalRef')}</FormLabel>
							<FormControl>
								<Input {...field} value={field.value ?? ''} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</FormDialog>
		</Form>
	)
}
